import { useState } from "react";
import { Pencil, Trash2, EyeOff, CheckCircle2, FileText, Globe, Loader2, Archive, ArchiveRestore, ChevronUp, ChevronDown, CalendarClock } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import { brl, formatDate, pct, imgVersion } from "@/lib/format";
import BoletoModal from "@/components/BoletoModal";

export default function AdminGoalsList({ metas, onEdit, onChanged, ordenarPorVencimento }) {
  const [viewing, setViewing] = useState(null);
  const [acting, setActing] = useState(null);

  const run = async (id, fn, successMsg) => {
    setActing(id);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(null);
    }
  };

  const togglePublicar = (meta) =>
    run(meta.id, async () => {
      await api.put(`/admin/metas/${meta.id}`, {
        referente: meta.referente,
        beneficiario: meta.beneficiario,
        cnpj: meta.cnpj,
        descricao: meta.descricao,
        valor_total: meta.valor_total,
        valor_arrecadado: meta.valor_arrecadado,
        vencimento: meta.vencimento,
        chave_pix: meta.chave_pix,
        ordem: meta.ordem,
        ativo: !meta.ativo,
      });
    }, meta.ativo ? "Meta despublicada (oculta do painel público)." : "Meta publicada! Já aparece no painel público.");

  const togglePago = (meta) =>
    run(meta.id, async () => {
      await api.patch(`/admin/metas/${meta.id}/pago`, { pago: !meta.pago_manual });
    }, meta.pago_manual ? "Marcação de pago removida." : "Meta marcada como PAGA.");

  const arquivar = (meta) =>
    run(meta.id, () => api.patch(`/admin/metas/${meta.id}/arquivar`, { arquivada: !meta.arquivada }),
      meta.arquivada ? "Meta restaurada. Use Publicar para exibi-la novamente." : "Meta arquivada. O histórico foi preservado.");

  const remove = (meta) => {
    if (!window.confirm(`Excluir a meta "${meta.referente}"?\n\nSe houver contribuições, ela será apenas ARQUIVADA (o histórico é preservado).`)) return;
    run(meta.id, async () => {
      const r = await api.delete(`/admin/metas/${meta.id}`);
      if (r.data?.arquivada) toast.info(r.data.detail); else toast.success("Meta excluída.");
    });
  };

  const arquivadas = metas.filter((m) => m.arquivada);
  const [showArq, setShowArq] = useState(false);
  const visiveis = showArq ? arquivadas : metas.filter((m) => !m.arquivada);

  const mover = (idx, dir) => {
    const lista = metas.filter((m) => !m.arquivada);
    const alvo = idx + dir;
    if (alvo < 0 || alvo >= lista.length) return;
    const ids = lista.map((m) => m.id);
    [ids[idx], ids[alvo]] = [ids[alvo], ids[idx]];
    run(lista[idx].id, () => api.post("/admin/metas/reordenar", { ids }), null);
  };

  if (metas.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500 text-lg">
        Nenhuma meta cadastrada. Use a aba "Nova Meta" para gerar o primeiro boleto.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button data-testid="goals-filter-ativas" onClick={() => setShowArq(false)} className={`px-4 py-2 rounded-xl text-sm font-bold border transition-colors ${!showArq ? "bg-blue-800 text-white border-blue-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
          Metas ({metas.length - arquivadas.length})
        </button>
        <button data-testid="goals-filter-arquivadas" onClick={() => setShowArq(true)} className={`px-4 py-2 rounded-xl text-sm font-bold border transition-colors inline-flex items-center gap-1.5 ${showArq ? "bg-blue-800 text-white border-blue-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
          <Archive className="w-4 h-4" /> Arquivadas ({arquivadas.length})
        </button>
      </div>
      {visiveis.length === 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500">
          {showArq ? "Nenhuma meta arquivada." : "Nenhuma meta ativa."}
        </div>
      )}
      {!showArq && (
        <p data-testid="goals-order-hint" className="text-sm text-slate-500 inline-flex items-center gap-2">
          <CalendarClock className="w-4 h-4" />
          {ordenarPorVencimento
            ? "Ordem automática por vencimento LIGADA (Configurações): as que vencem primeiro aparecem antes. As setas definem a ordem de desempate."
            : "Use as setas para definir a ordem em que as metas aparecem na página pública."}
        </p>
      )}
      {visiveis.map((meta, idx) => {
        const pago = meta.status === "pago";
        const busy = acting === meta.id;
        return (
          <div
            key={meta.id}
            data-testid={`admin-goal-row-${meta.id}`}
            className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex flex-col lg:flex-row gap-5"
          >
            {!showArq && (
              <div className="flex lg:flex-col gap-1 shrink-0 self-start">
                <button data-testid={`admin-move-up-${meta.id}`} onClick={() => mover(idx, -1)} disabled={busy || idx === 0} aria-label="Subir" className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 disabled:opacity-30 flex items-center justify-center text-slate-700"><ChevronUp className="w-5 h-5" /></button>
                <span className="w-10 h-8 flex items-center justify-center text-xs font-bold text-slate-400">{idx + 1}º</span>
                <button data-testid={`admin-move-down-${meta.id}`} onClick={() => mover(idx, 1)} disabled={busy || idx === visiveis.length - 1} aria-label="Descer" className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 disabled:opacity-30 flex items-center justify-center text-slate-700"><ChevronDown className="w-5 h-5" /></button>
              </div>
            )}
            {meta.tem_boleto && (
              <button
                type="button"
                data-testid={`admin-view-boleto-thumb-${meta.id}`}
                onClick={() => setViewing(meta)}
                className="shrink-0 w-full lg:w-32 h-32 rounded-xl overflow-hidden border border-slate-200 hover:ring-2 hover:ring-blue-400 transition-shadow"
                title="Visualizar boleto"
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/metas/${meta.id}/boleto.png${imgVersion(meta)}`}
                  alt={`Boleto ${meta.referente}`}
                  onError={(e) => { e.currentTarget.closest("button").style.display = "none"; }}
                  className="w-full h-full object-cover object-top"
                />
              </button>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-heading text-lg font-bold text-slate-900">{meta.referente}</h3>
                {pago ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                    <CheckCircle2 className="w-3.5 h-3.5" /> PAGO{meta.pago_manual ? " (manual)" : ""}
                  </span>
                ) : (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                    EM ABERTO
                  </span>
                )}
                {meta.arquivada && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-800 text-white">
                    <Archive className="w-3.5 h-3.5" /> ARQUIVADA
                  </span>
                )}
                {!meta.ativo && !meta.arquivada && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-600 border border-slate-200">
                    <EyeOff className="w-3.5 h-3.5" /> NÃO PUBLICADA
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-slate-500">
                Vencimento {formatDate(meta.vencimento)} · Ordem {meta.ordem}
              </p>
              <div className="mt-3 flex items-center gap-3">
                <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${pago ? "bg-emerald-500" : "bg-blue-700"}`}
                    style={{ width: `${meta.percentual}%` }}
                  />
                </div>
                <span className="text-sm font-bold text-slate-700 whitespace-nowrap">{pct(meta.percentual)}%</span>
              </div>
              <p className="mt-2 text-sm text-slate-600">
                <span className="font-bold text-emerald-700">{brl(meta.valor_arrecadado)}</span> de{" "}
                <span className="font-bold">{brl(meta.valor_total)}</span> · faltam{" "}
                <span className="font-bold text-blue-900">{brl(meta.valor_restante)}</span>
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0 lg:flex-col lg:items-stretch">
              <button
                data-testid={`admin-view-boleto-${meta.id}`}
                onClick={() => setViewing(meta)}
                disabled={busy}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm border border-slate-200 transition-colors disabled:opacity-60"
              >
                <FileText className="w-4 h-4" /> Visualizar
              </button>
              <button
                data-testid={`admin-edit-goal-${meta.id}`}
                onClick={() => onEdit(meta)}
                disabled={busy}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm border border-slate-200 transition-colors disabled:opacity-60"
              >
                <Pencil className="w-4 h-4" /> Editar
              </button>
              <button
                data-testid={`admin-publish-goal-${meta.id}`}
                onClick={() => togglePublicar(meta)}
                disabled={busy || meta.arquivada}
                title={meta.arquivada ? "Restaure a meta para publicar" : undefined}
                className={`inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl font-semibold text-sm border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  meta.ativo
                    ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200"
                    : "bg-blue-800 hover:bg-blue-700 text-white border-blue-800"
                }`}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : meta.ativo ? <EyeOff className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                {meta.ativo ? "Despublicar" : "Publicar"}
              </button>
              <button
                data-testid={`admin-mark-paid-${meta.id}`}
                onClick={() => togglePago(meta)}
                disabled={busy || meta.arquivada}
                title={meta.arquivada ? "Restaure a meta para alterar" : undefined}
                className={`inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl font-semibold text-sm border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  meta.pago_manual
                    ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200"
                    : "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600"
                }`}
              >
                <CheckCircle2 className="w-4 h-4" />
                {meta.pago_manual ? "Desfazer pago" : "Marcar como pago"}
              </button>
              <button
                data-testid={`admin-archive-goal-${meta.id}`}
                onClick={() => arquivar(meta)}
                disabled={busy}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm border border-slate-200 transition-colors disabled:opacity-60"
              >
                {meta.arquivada ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                {meta.arquivada ? "Restaurar" : "Arquivar"}
              </button>
              <button
                data-testid={`admin-delete-goal-${meta.id}`}
                onClick={() => remove(meta)}
                disabled={busy}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 font-semibold text-sm border border-rose-200 transition-colors disabled:opacity-60"
              >
                <Trash2 className="w-4 h-4" /> Excluir
              </button>
            </div>
          </div>
        );
      })}

      {viewing && <BoletoModal meta={viewing} open={!!viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
